import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trophy, PartyPopper, Sparkles, ArrowRight } from "lucide-react";
import { API } from "@/App";
import { fireConfetti } from "@/lib/confetti";
import { WHATSAPP_SALES_URL } from "@/lib/sales";

const STORAGE_KEY = "fixedraws_congrats_popup_v1";
const DELAY_MS = 2400; // give the page a moment to render

/**
 * Shows up to `frequency_per_day` times per visitor per calendar day.
 * State persisted in localStorage: { day: 'YYYY-MM-DD', count: n }
 */
const readState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
const writeState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
};
const todayKey = () => new Date().toISOString().slice(0, 10);

const CongratsPopup = () => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/congrats-popup`)
      .then((r) => setContent(r.data))
      .catch(() => setContent(null));
  }, []);

  useEffect(() => {
    if (!content || !content.enabled) return;
    // Never show on admin routes
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return;
    const limit = Number(content.frequency_per_day ?? 2) || 0;
    if (limit <= 0) return;

    const state = readState() || { day: todayKey(), count: 0 };
    if (state.day !== todayKey()) {
      state.day = todayKey();
      state.count = 0;
    }
    if (state.count >= limit) return;

    const t = setTimeout(() => {
      setOpen(true);
      state.count = (state.count || 0) + 1;
      writeState(state);
      // Track open
      axios.post(`${API}/congrats/track`, { event: "popup_open" }).catch(() => {});
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, [content]);

  if (!content) return null;

  const go = () => {
    if (ctaRef.current) fireConfetti(ctaRef.current);
    axios.post(`${API}/congrats/track`, { event: "popup_click" }).catch(() => {});
    setTimeout(() => {
      setOpen(false);
      // Every popup CTA now opens WhatsApp with Madam Precious.
      const link = /^https?:\/\//i.test(content.cta_link || "") ? content.cta_link : WHATSAPP_SALES_URL;
      window.open(link, "_blank", "noopener,noreferrer");
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-0 rounded-3xl shadow-2xl"
        data-testid="congrats-popup"
      >
        {/* Celebration header */}
        <div
          className="relative px-6 pt-8 pb-7 text-center overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #052e16 0%, #065f46 45%, #b45309 100%)",
          }}
        >
          {/* Confetti sparkles */}
          <div className="pointer-events-none absolute inset-0 opacity-70">
            {[
              { l: "10%", t: "22%", c: "#fbbf24" },
              { l: "28%", t: "72%", c: "#f472b6" },
              { l: "50%", t: "18%", c: "#34d399" },
              { l: "72%", t: "68%", c: "#60a5fa" },
              { l: "88%", t: "24%", c: "#fbbf24" },
              { l: "18%", t: "48%", c: "#f97316" },
              { l: "85%", t: "50%", c: "#f472b6" },
            ].map((d, i) => (
              <span
                key={i}
                className="absolute h-2 w-2 rounded-full animate-pulse"
                style={{ left: d.l, top: d.t, background: d.c, animationDelay: `${i * 250}ms` }}
              />
            ))}
          </div>
          <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-400/40 blur-3xl" />

          <div className="relative z-10 flex flex-col items-center">
            <div className="relative mb-3">
              <div className="absolute inset-0 rounded-full bg-amber-400 blur-2xl opacity-60 animate-pulse" />
              <div className="relative h-16 w-16 rounded-full bg-gradient-to-br from-amber-300 via-amber-500 to-amber-700 flex items-center justify-center shadow-inner">
                <Trophy className="h-8 w-8 text-white drop-shadow" />
              </div>
              <Sparkles className="absolute -top-1 -right-2 h-5 w-5 text-amber-200 animate-pulse" />
            </div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold tracking-widest uppercase text-amber-200 bg-amber-500/15 border border-amber-300/40 rounded-full px-3 py-1 mb-3">
              <PartyPopper className="h-3.5 w-3.5" />
              Fixedraws
            </span>
            <DialogTitle
              className="text-3xl font-extrabold text-white leading-tight uppercase tracking-wide"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.35)" }}
              data-testid="congrats-popup-title"
            >
              {content.title || "Congratulations!"}
            </DialogTitle>
            {content.subtitle ? (
              <DialogDescription className="mt-2 text-amber-200 font-semibold text-sm" data-testid="congrats-popup-subtitle">
                {content.subtitle}
              </DialogDescription>
            ) : (
              <DialogDescription className="sr-only">Winning celebration announcement</DialogDescription>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 text-center bg-white">
          <p
            className="text-sm text-gray-700 whitespace-pre-line leading-relaxed mb-5"
            data-testid="congrats-popup-message"
          >
            {content.message}
          </p>
          <Button
            ref={ctaRef}
            onClick={go}
            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-extrabold h-12 rounded-full shadow-lg shadow-emerald-500/20"
            data-testid="congrats-popup-cta"
          >
            {content.cta_text || "See picks"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <button
            onClick={() => setOpen(false)}
            className="text-xs text-gray-400 mt-3 hover:underline"
            data-testid="congrats-popup-close"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CongratsPopup;
